import { jwtDecode } from '../jwt/JwtDecoder';
import Cookies from 'js-cookie';
import { ILoggerDetails } from '../types';
import { LogDebug } from './Logging';
import { ACCESS_TOKEN_COOKIE_NAME } from '../constants/Constants';

// Global timeout ID to prevent race conditions with multiple setTimeout calls
let _backgroundRefreshTimeoutId: number | NodeJS.Timeout | null = null;

/**
 * Returns timing information for token refresh logic based on expiration time.
 * Calculates when a token should be refreshed based on a 25-minute window before expiration.
 *
 * @param {number} expirationTime - The token expiration time in seconds (Unix timestamp)
 * @returns {object} Object containing timing calculations including current time, expiration time in milliseconds, refresh window, and time until refresh window
 */
export function getRefreshTimingInfo(expirationTime: number) {
  const currentTime = new Date().getTime(); // Current time in milliseconds
  const expirationTimeInMilliseconds = expirationTime * 1000; // Convert expiration time to milliseconds
  const REFRESH_WINDOW_MS = 25 * 60 * 1000;
  const timeUntilRefreshWindow = expirationTimeInMilliseconds - currentTime - REFRESH_WINDOW_MS;
  return {
    currentTime,
    expirationTimeInMilliseconds,
    REFRESH_WINDOW_MS,
    timeUntilRefreshWindow,
  };
}

/**
 * Triggers the logic to refresh the authentication token in the background.
 * This function is designed to be called client-side in the browser and returns a promise
 * that resolves based on the HTTP response status.
 *
 * @param {string} emReAuthenticationServiceBaseUrl - The base URL for the authentication service.
 * @param {ILoggerDetails} [logger] - Optional logger for logging messages.
 * @returns {Promise<boolean>} - Promise that resolves to true if token refresh was successful, false otherwise.
 */
export const triggerClientSideAccessTokenRefresh = async (
  emReAuthenticationServiceBaseUrl: string,
  logger?: ILoggerDetails
): Promise<boolean> => {
  LogDebug(logger, 'Triggering client-side silent token refresh');

  try {
    const response = await fetch(`${emReAuthenticationServiceBaseUrl}/RefreshToken`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
      method: 'POST',
    });

    if (response.ok) {
      LogDebug(logger, 'Token refreshed successfully');

      const em_at = Cookies.get(ACCESS_TOKEN_COOKIE_NAME); // to verify if auth cookie has been created
      return !!em_at;
    } else {
      LogDebug(logger, 'Failed to refresh token');
      return false;
    }
  } catch (error) {
    LogDebug(logger, `Error during token refresh: ${error}`);
    return false;
  }
};

/**
 * Checks if the provided expiration time is less than 25 minutes from the current time and in the future.
 * If so, triggers logic to refresh the authentication token in the background.
 * !Important: This function is designed to be called client-side in the browser and does not return a value.
 *
 * @param {string} emReAuthenticationServiceBaseUrl - The base URL for the authentication service.
 * @param {ILoggerDetails} [logger] - Optional logger for logging messages.
 * @returns {void} - No return value.
 */
export const tryBackgroundRefreshToken = (emReAuthenticationServiceBaseUrl: string, logger?: ILoggerDetails) => {
  LogDebug(logger, 'tryBackgroundRefreshToken() called');

  // Clear any existing timeout to prevent race conditions
  if (_backgroundRefreshTimeoutId) {
    clearTimeout(_backgroundRefreshTimeoutId);
    _backgroundRefreshTimeoutId = null;
    LogDebug(logger, 'tryBackgroundRefreshToken() :: Cleared existing background refresh timeout');
  }

  // Helper to get the current expiration from the em_at cookie
  const getExpiration = () => {
    const emAtCookie = Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
    return jwtDecode(emAtCookie)?.exp;
  };

  // This function is called immediately or by setTimeout, and always re-checks expiration
  const checkAndTriggerAccessTokenRefresh = () => {
    LogDebug(logger, 'Token refresh setTimeout callback called');

    // Clear the timeout ID since this callback is now executing
    _backgroundRefreshTimeoutId = null;

    // Re-check the expiration time
    // This is important because the timeout may have fired but the window condition is no longer met
    // (e.g., the token may have been refreshed elsewhere or the token may have expired)
    const currentExpirationTime = getExpiration();

    if (!currentExpirationTime) {
      LogDebug(
        logger,
        'No em_at cookie expiration time found in token refresh setTimeout callback. Skipping token refresh.'
      );
      return;
    }

    const { currentTime, expirationTimeInMilliseconds, REFRESH_WINDOW_MS } =
      getRefreshTimingInfo(currentExpirationTime);

    if (expirationTimeInMilliseconds - currentTime < REFRESH_WINDOW_MS) {
      triggerClientSideAccessTokenRefresh(emReAuthenticationServiceBaseUrl, logger);
    } else {
      // Log if the timeout fired but the window condition is no longer met(token may have been refreshed elsewhere)
      LogDebug(logger, 'Token refresh window condition not met after timeout, skipping trigger.');
    }
  };

  // Initial scheduling logic
  const expirationTime = getExpiration();
  if (!expirationTime) {
    LogDebug(logger, 'No em_at cookie expiration time found. Skipping token refresh.');
    return;
  }

  LogDebug(logger, `em_at cookie expiration time found: ${expirationTime}`);

  const { currentTime, expirationTimeInMilliseconds, REFRESH_WINDOW_MS, timeUntilRefreshWindow } =
    getRefreshTimingInfo(expirationTime);

  // Check if the expiration time is in the future and within the 25-minute window
  if (expirationTimeInMilliseconds > currentTime) {
    if (timeUntilRefreshWindow > 0) {
      _backgroundRefreshTimeoutId = setTimeout(checkAndTriggerAccessTokenRefresh, timeUntilRefreshWindow) as NodeJS.Timeout | number;
      LogDebug(logger, `Token refresh scheduled in ${timeUntilRefreshWindow} ms`);
    } else if (expirationTimeInMilliseconds - currentTime < REFRESH_WINDOW_MS) {
      triggerClientSideAccessTokenRefresh(emReAuthenticationServiceBaseUrl, logger);
    }
  }
};

/**
 * Clears any pending background token refresh timeout.
 * Useful for cleanup when components unmount or when manual token refresh is performed.
 *
 * @param {ILoggerDetails} [logger] - Optional logger for logging messages.
 * @returns {void} - No return value.
 */
export const clearBackgroundRefreshTimeout = (logger?: ILoggerDetails) => {
  if (_backgroundRefreshTimeoutId) {
    clearTimeout(_backgroundRefreshTimeoutId);
    _backgroundRefreshTimeoutId = null;
    LogDebug(logger, 'Background refresh timeout cleared');
  }
};
